import { useMemo, useState } from 'react';
import { TScoreSkillLevel, TSimulationType } from '@/types';
import { FeedbackSection } from '@/components';

/**
 * When feedback is collapsed, only the first item from the first section is shown.
 *
 * This keeps the card compact while still giving the user a preview of the
 * available coaching content.
 */
const COLLAPSED_ITEM_COUNT = 1;

/**
 * Input contract for the score row card feature hook.
 *
 * The hook accepts a mix of content, view-state flags, and score metadata so it
 * can centralize display logic for the ScoreRowCard presentation component.
 */
interface UseScoreRowCardFeatureProps {
  /** Structured feedback sections rendered in the card body. */
  feedback?: FeedbackSection[];
  /** High-level row type used for special rendering rules like violations. */
  type?: 'violation' | 'other';
  /** Whether to show the skill level badge */
  showSkillLevelBadge?: boolean;
  /** Enables or disables the expand/collapse behavior for feedback. */
  showToggle?: boolean;
  /** Forces the feedback content into its expanded state. */
  forceExpanded?: boolean;
  /** Simulation category that affects variant and skill-tag visibility. */
  simulationType?: TSimulationType;
  /** Skill level label displayed for eligible simulation types. */
  skillLevel?: TScoreSkillLevel | null;
  /** Loading state for placeholder UI while score data is being resolved. */
  isLoading?: boolean;
  /** Pass/fail result used when computing certification/practice styling. */
  pass?: boolean | null;
  /** Raw score type used for special-case rows such as violations. */
  scoreType?: string;
}

/**
 * Return contract for consumers of the score row card feature hook.
 *
 * These values are intentionally presentation-oriented so the component can stay
 * focused on rendering while the hook owns the branching logic.
 */
interface UseScoreRowCardFeatureReturn {
  /** Whether the feedback list is currently expanded. */
  isExpanded: boolean;
  /** Whether there is additional feedback hidden behind the toggle. */
  hasMoreFeedback: boolean;
  /** Feedback subset that should be rendered in the current UI state. */
  visibleFeedback: FeedbackSection[];
  /** Visual variant for the score card styling. */
  scoreCardVariant: 'violation' | 'pass' | 'fail' | 'simple';
  /** Whether to show the skill level badge, or the skill level value to display. */  
  showSkillLevelBadge?: boolean;
  /** Whether to show the skill tag, or the skill level value to display. */
  showSkillLevelTag: boolean | TScoreSkillLevel;
  /** Toggles expanded/collapsed state for feedback. */
  handleToggle: () => void;
}

/**
 * Hook that manages the state and computed values for the ScoreRowCard component.
 *
 * Responsibilities:
 * - track whether feedback is expanded
 * - determine if a toggle should expose more feedback
 * - compute the visible feedback slice for the current state
 * - derive the card's visual variant from score metadata
 * - decide whether a skill level tag should be shown
 */
export const useScoreRowCardFeature = ({
  feedback = [],
  type = 'other',
  showSkillLevelBadge = false,
  showToggle = true,
  forceExpanded = false,
  simulationType,
  skillLevel,
  isLoading = false,
  pass = null,
  scoreType,
}: UseScoreRowCardFeatureProps): UseScoreRowCardFeatureReturn => {
  // ================================
  // Local state
  // ================================
  // Tracks whether the feedback section is expanded by user interaction.
  const [isExpanded, setIsExpanded] = useState(false);

  // ================================
  // Derived feedback metadata
  // ================================
  /**
   * Determines whether the card has additional feedback beyond the collapsed preview.
   *
   * Violation rows intentionally disable the "more feedback" behavior, even if
   * feedback content exists, because they follow a different presentation model.
   */
  const hasMoreFeedback = useMemo(() => {
    if (type === 'violation') return false;

    return (
      feedback.length > 0 &&
      (feedback[0].items.length > COLLAPSED_ITEM_COUNT || feedback.length > 1)
    );
  }, [feedback, type]);

  // ================================
  // Event handlers
  // ================================
  /**
   * Flips the expanded state for feedback content.
   *
   * The component decides when to show the toggle button; this handler only
   * manages the state transition once triggered.
   */
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // ================================
  // Derived feedback content
  // ================================
  /**
   * Computes the feedback sections that should be rendered.
   *
   * Behavior summary:
   * - when toggling is disabled, show all feedback
   * - when expansion is forced, show all feedback
   * - when expanded, show all feedback
   * - when collapsed, show only the first item of the first section
   */
  const visibleFeedback = useMemo(() => {
    if (!showToggle || forceExpanded) {
      return feedback; // Show all feedback if toggle is disabled or forceExpanded is true
    }

    if (isExpanded) {
      return feedback;
    }

    return feedback.length > 0
      ? [
          {
            ...feedback[0],
            items: feedback[0].items.slice(0, COLLAPSED_ITEM_COUNT),
          },
        ]
      : [];
  }, [feedback, isExpanded, showToggle, forceExpanded]);

  /**
   * Maps score metadata to the card variant used by the UI.
   *
   * Priority order:
   * 1. explicit violation score type
   * 2. pass/fail styling for certification and practice simulations
   * 3. simple fallback for all other simulation types
   */
  const scoreCardVariant = useMemo((): 'violation' | 'pass' | 'fail' | 'simple' => {
    if (scoreType === 'violation') return 'violation';

    if (simulationType === 'certification' || simulationType === 'practice') {
      if (!pass) return 'fail';
      return pass === true ? 'pass' : pass === false ? 'fail' : 'simple';
    }

    return 'simple';
  }, [scoreType, simulationType, pass]);

  /**
   * Determines whether the skill level tag should be rendered.
   *
   * Return shape:
   * - `true` while loading, so the UI can render a loading placeholder
   * - `false` when the tag should be hidden
   * - the concrete skill level string when the tag should be shown
   */
  const showSkillLevelTag = useMemo((): boolean | TScoreSkillLevel => {
    if (!showSkillLevelBadge) return false; // Explicit prop to disable skill level badge
    if (isLoading) return true; // Show loading state for skill level tag while loading

    // Violation rows never display a skill level tag.
    if (type === 'violation') {
      return false;
    }

    // Certification and practice simulations intentionally suppress this tag.
    if (simulationType === 'certification' || simulationType === 'practice') {
      return false;
    }

    // Without a value to display, the tag should remain hidden.
    if (skillLevel === null || skillLevel === undefined) {
      return false;
    }

    // For eligible rows, return the label directly for rendering.
    return skillLevel;
  }, [showSkillLevelBadge, type, simulationType, skillLevel, isLoading]);

  // ================================
  // Public contract
  // ================================
  return {
    isExpanded,
    hasMoreFeedback,
    visibleFeedback,
    scoreCardVariant,
    showSkillLevelTag,
    handleToggle,
  };
};
